import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';

import { cx, fieldControlClass } from './shared';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      description,
      error,
      id,
      required,
      className,
      wrapperClassName,
      children,
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const selectId = id ?? `select-${generatedId}`;
    const descriptionId = description ? `${selectId}-description` : undefined;
    const errorId = error ? `${selectId}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId].filter(Boolean).join(' ');

    return (
      <div className={cx('grid gap-field-gap', wrapperClassName)}>
        <label htmlFor={selectId} className="text-control font-semibold text-ink-strong">
          {label}
          {required ? <span className="text-status-invalid"> *</span> : null}
        </label>
        {description ? (
          <p id={descriptionId} className="text-meta text-ink-muted">
            {description}
          </p>
        ) : null}
        <select
          ref={ref}
          id={selectId}
          required={required}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy || undefined}
          className={cx(fieldControlClass, 'cursor-pointer', className)}
          {...rest}
        >
          {children}
        </select>
        {error ? (
          <p id={errorId} className="text-meta font-semibold text-status-invalid">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

Select.displayName = 'Select';
